/**
 * useMapLifecycle.js
 * Handles Google Maps instance initialization, event listeners, and lifecycle
 */

import { ref, onUnmounted } from 'vue'

export function useMapLifecycle() {
  const map = ref(null)
  const listeners = []

  /**
   * Initialize Google Maps instance
   * @param {HTMLElement} container - DOM element to mount map
   * @param {Object} options - Map configuration options
   */
  const initMap = (container, options = {}) => {
    const defaultOptions = {
      center: { lat: 0, lng: 0 },
      zoom: 20,
      zoomControl: true,
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      disableDefaultUI: true,
      gestureHandling: 'greedy',
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM
      },
      tilt: 0
    }

    map.value = new google.maps.Map(container, {
      ...defaultOptions,
      ...options
    })

    // Create custom OverlayView for projection calculations
    function Overlay() {
      this.setMap(map.value)
    }
    Overlay.prototype = new google.maps.OverlayView()
    map.value.overlay = new Overlay()

    return map.value
  }

  /**
   * Add event listener to map and track for cleanup
   * @param {string} eventName - Google Maps event name
   * @param {Function} handler - Event handler function
   */
  const addListener = (eventName, handler) => {
    if (!map.value) {
      console.warn('addListener: map not initialized')
      return null
    }

    const listener = map.value.addListener(eventName, handler)
    listeners.push(listener)
    return listener
  }

  /**
   * Remove specific listener
   * @param {Object} listener - Google Maps listener object
   */
  const removeListener = (listener) => {
    if (listener) {
      google.maps.event.removeListener(listener)
      const index = listeners.indexOf(listener)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * Remove all tracked listeners
   */
  const removeAllListeners = () => {
    listeners.forEach(listener => {
      google.maps.event.removeListener(listener)
    })
    listeners.length = 0
  }

  /**
   * Cleanup on component unmount
   */
  onUnmounted(() => {
    removeAllListeners()
    if (map.value) {
      // Clear map instance
      map.value = null
    }
  })

  return {
    map,
    initMap,
    addListener,
    removeListener,
    removeAllListeners
  }
}
